const elementCache = new Map();

export const getElementById = (id, useCache = true) => {
  if (!id || typeof id !== 'string') {
    return null;
  }

  if (useCache && elementCache.has(id)) {
    const cached = elementCache.get(id);
    if (cached && document.body.contains(cached)) {
      return cached;
    }
    elementCache.delete(id);
  }

  try {
    const element = document.getElementById(id);
    if (element && useCache) {
      elementCache.set(id, element);
    }
    return element;
  } catch (e) {
    console.warn(`Error getting element by id "${id}":`, e);
    return null;
  }
};

export const getAppContainer = () => {
  return getElementById('app-container');
};

export const findScrollableParent = (node) => {
  if (!node) return null;

  let current = node;
  while (current && current !== document.body) {
    try {
      const view = current && current.ownerDocument ? current.ownerDocument.defaultView : null;
      const style = view ? view.getComputedStyle(current) : null;
      const overflowY = style ? style.getPropertyValue('overflow-y') : null;
      const canScroll = overflowY === 'auto' || overflowY === 'scroll';
      
      if (canScroll && current.scrollHeight > current.clientHeight) {
        return current;
      }
      current = current.parentNode;
    } catch (e) {
      current = current.parentNode;
    }
  }
  return null;
};

export const getScrollableElement = () => {
  const appContainer = getAppContainer();
  if (!appContainer) return null;
  return findScrollableParent(appContainer);
};

export const scrollToTop = (options = {}) => {
  const { behavior = 'smooth', target } = options;

  try {
    if (target) {
      if (target.scrollTo) {
        target.scrollTo({ top: 0, behavior });
        return;
      }
      target.scrollTop = 0;
      return;
    }

    const scrollableElement = getScrollableElement();
    
    if (scrollableElement && scrollableElement.scrollTo) {
      scrollableElement.scrollTo({ top: 0, behavior });
      return;
    }

    if (typeof window !== 'undefined' && window.scrollTo) {
      window.scrollTo({ top: 0, behavior });
      return;
    }

    if (document && document.documentElement) {
      document.documentElement.scrollTop = 0;
      if (document.body) {
        document.body.scrollTop = 0;
      }
    }
  } catch (e) {
    console.warn('Error scrolling to top:', e);
  }
};

export const scrollToElement = (elementOrId, options = {}) => {
  const {
    behavior = 'smooth',
    block = 'start',
    inline = 'nearest',
    offset = 0
  } = options;

  try {
    let element = null;

    if (typeof elementOrId === 'string') {
      element = getElementById(elementOrId);
    } else if (elementOrId instanceof HTMLElement) {
      element = elementOrId;
    }

    if (!element) {
      console.warn('Element not found for scrolling');
      return;
    }

    if (element.scrollIntoView) {
      element.scrollIntoView({
        behavior,
        block,
        inline
      });
      return;
    }

    const rect = element.getBoundingClientRect();
    const scrollableElement = findScrollableParent(element) || window;
    
    const scrollTop = rect.top + (scrollableElement.pageYOffset || scrollableElement.scrollY || 0) - offset;

    if (scrollableElement.scrollTo) {
      scrollableElement.scrollTo({
        top: scrollTop,
        behavior
      });
    } else if (scrollableElement === window) {
      window.scrollTo({
        top: scrollTop,
        behavior
      });
    } else {
      scrollableElement.scrollTop = scrollTop;
    }
  } catch (e) {
    console.warn('Error scrolling to element:', e);
  }
};

export const clearElementCache = () => {
  elementCache.clear();
};

